/**
 * Station lot equipment display (refill §4.1 + washing §4.2).
 * UI color groups: protocol §4.0.
 */

import type { IStationEquipmentTelemetry } from 'utils/interfaces'
import { lotModelEquipmentProfile, supportsExtendedDockSteps } from 'utils/stationLotModel'

export type StationEquipmentValue = string | boolean | number | null | undefined

/** Protocol §4.0 UI color groups (+ neutral for unmatched non-actuator tokens). */
export type EquipmentUiGroup =
  | 'ok'
  | 'idle'
  | 'active'
  | 'fault'
  | 'unknown'
  | 'actuator_fault'
  | 'neutral'

const LOT_TYPE_WASHING = 1
const LOT_TYPE_REFILL = 4

function profileFromLotModel(lotModel?: string | null): 'refill' | 'washing' | 'unknown' {
  const profile = lotModelEquipmentProfile(lotModel)
  if (profile === 'refill' || profile === 'washing') return profile
  return 'unknown'
}

const ACTUATOR_KEYS = new Set(['water_actuator', 'waste_actuator', 'charger_linak', 'eld2'])

/** Exact token → UI group (§4.0 table). */
const TOKEN_GROUPS: Record<string, Exclude<EquipmentUiGroup, 'actuator_fault' | 'neutral'>> = {
  ok: 'ok',
  dry: 'ok',
  closed: 'idle',
  off: 'idle',
  idle: 'idle',
  stop: 'idle',
  stopped: 'idle',
  unlocked: 'idle',
  locked: 'idle',
  open: 'active',
  on: 'active',
  working: 'active',
  opening: 'active',
  closing: 'active',
  stop_pending: 'active',
  wet: 'active',
  fault: 'fault',
  leak: 'fault',
  high: 'fault',
  error: 'fault',
  unknown: 'unknown',
}

/** Actuator enum set (§4.0 rule 2); anything else → actuator_fault. */
const ACTUATOR_ENUM = new Set(['idle', 'working', 'unknown', 'error'])

const EQUIPMENT_LABELS: Record<string, string> = {
  fill_valve: 'Fill valve',
  leak_f1: 'Leak F1',
  leak_f2: 'Leak F2',
  water_actuator: 'Water actuator',
  drain_pump: 'Drain pump',
  dirty_water_low: 'Drain tank low',
  dirty_water_high: 'Drain tank high',
  VALVE_WATER_FILLING: 'Water filling valve',
  VALVE_WATER_CUTOFF: 'Water cutoff valve',
  VALVE_WASTE_TANK_WASHING: 'Waste tank washing valve',
  VALVE_FLOOR_FRONT_NOZZLES: 'Floor front nozzles',
  VALVE_FLOOR_BACK_NOZZLES: 'Floor back nozzles',
  VALVE_LEFT_FRONT_NOZZLES: 'Left front nozzles',
  VALVE_LEFT_BACK_NOZZLES: 'Left back nozzles',
  VALVE_RIGHT_FRONT_NOZZLES: 'Right front nozzles',
  VALVE_RIGHT_BACK_NOZZLES: 'Right back nozzles',
  VALVE_SUCTION_PIPE_NOZZLES: 'Suction pipe nozzles',
  VALVE_TOP_NOZZLES: 'Top nozzles',
  VALVE_DIRTY_WATER_TANK: 'Dirty water tank valve',
  PUMP_1_RELAY: 'Pump 1 relay',
  PUMP_2_RELAY: 'Pump 2 relay',
  PUMP_3_RELAY: 'Pump 3 relay',
  PUMP_1_PRESSURE: 'Pump 1 pressure',
  PUMP_2_PRESSURE: 'Pump 2 pressure',
  gate: 'Gate',
  gate_lock: 'Gate lock',
  gate_fault: 'Gate fault',
  drying_fans: 'Drying fans',
  dirty_water_level: 'Dirty water level',
  waste_actuator: 'Waste actuator',
  charger_linak: 'Charger Linak',
  eld2: 'ELD2 tank wash',
}

const REFILL_ORDER = [
  'fill_valve',
  'leak_f1',
  'leak_f2',
  'drain_pump',
  'dirty_water_low',
  'dirty_water_high',
  'water_actuator',
] as const

const WASHING_ORDER = [
  'VALVE_WATER_FILLING',
  'VALVE_WATER_CUTOFF',
  'VALVE_WASTE_TANK_WASHING',
  'VALVE_FLOOR_FRONT_NOZZLES',
  'VALVE_FLOOR_BACK_NOZZLES',
  'VALVE_LEFT_FRONT_NOZZLES',
  'VALVE_LEFT_BACK_NOZZLES',
  'VALVE_RIGHT_FRONT_NOZZLES',
  'VALVE_RIGHT_BACK_NOZZLES',
  'VALVE_SUCTION_PIPE_NOZZLES',
  'VALVE_TOP_NOZZLES',
  'VALVE_DIRTY_WATER_TANK',
  'PUMP_1_RELAY',
  'PUMP_1_PRESSURE',
  'PUMP_2_RELAY',
  'PUMP_2_PRESSURE',
  'PUMP_3_RELAY',
  'gate',
  'gate_lock',
  'gate_fault',
  'drying_fans',
  'dirty_water_level',
  'water_actuator',
  'waste_actuator',
  'charger_linak',
  'eld2',
] as const

export type EquipmentDisplayGroup = {
  id: string
  title: string
  entries: Array<[string, StationEquipmentValue]>
}

function humanizeKey(key: string): string {
  if (EQUIPMENT_LABELS[key]) {
    return EQUIPMENT_LABELS[key]
  }
  return key
    .replace(/^VALVE_/, '')
    .replace(/^PUMP_/, 'Pump ')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, ch => ch.toUpperCase())
}

export function equipmentFieldLabel(key: string): string {
  return humanizeKey(key)
}

/** Drop legacy boolean/string pairs no longer published (§4.0 p.3). */
const LEGACY_EQUIPMENT_KEYS = new Set([
  'water_actuator_ok',
  'water_actuator_error',
  'waste_actuator_ok',
  'waste_actuator_error',
])

export function isLegacyEquipmentKey(key: string): boolean {
  return LEGACY_EQUIPMENT_KEYS.has(key)
}

/**
 * Map equipment field value to §4.0 UI color group.
 * Missing keys are not passed here (widget hidden by caller).
 */
export function classifyEquipmentUiGroup(
  key: string,
  value: StationEquipmentValue
): EquipmentUiGroup {
  if (value === null || value === undefined || value === '') {
    return 'neutral'
  }

  // RS gate_fault may arrive as int code (0 = no fault).
  if (key === 'gate_fault' && typeof value === 'number') {
    return value === 0 ? 'ok' : 'fault'
  }

  if (typeof value === 'boolean' || typeof value === 'number') {
    return 'neutral'
  }

  const token = String(value).trim()
  if (!token) {
    return 'neutral'
  }

  // 402-1 intermediate drain tank: low=water present (info), high=near overflow (alarm).
  if (key === 'dirty_water_high' && token === 'wet') {
    return 'fault'
  }
  if (key === 'dirty_water_low' && token === 'wet') {
    return 'active'
  }

  if (key === 'gate_fault') {
    if (token === '0' || token === 'ok') return 'ok'
    if (token === 'unknown') return 'unknown'
    return 'fault'
  }

  const exact = TOKEN_GROUPS[token]
  if (ACTUATOR_KEYS.has(key)) {
    if (ACTUATOR_ENUM.has(token)) {
      return exact || 'neutral'
    }
    return 'actuator_fault'
  }

  return exact || 'neutral'
}

function sortKeys(keys: string[], preferred: readonly string[]): string[] {
  const rank = new Map(preferred.map((k, index) => [k, index]))
  return [...keys].sort((a, b) => {
    const ra = rank.has(a) ? rank.get(a)! : 1000
    const rb = rank.has(b) ? rank.get(b)! : 1000
    if (ra !== rb) return ra - rb
    return a.localeCompare(b)
  })
}

function groupIdForKey(key: string): string {
  if (key.startsWith('VALVE_') || key === 'fill_valve') return 'valves'
  if (key.startsWith('PUMP_') || key === 'drain_pump') return 'pumps'
  if (
    key.startsWith('leak_') ||
    key === 'dirty_water_level' ||
    key === 'dirty_water_low' ||
    key === 'dirty_water_high'
  ) {
    return 'sensors'
  }
  if (
    key.includes('actuator') ||
    key === 'charger_linak' ||
    key === 'eld2' ||
    key === 'gate' ||
    key === 'gate_lock' ||
    key === 'gate_fault' ||
    key === 'drying_fans'
  ) {
    return 'actuators'
  }
  return 'other'
}

const GROUP_TITLES: Record<string, string> = {
  valves: 'Valves',
  pumps: 'Pumps',
  sensors: 'Sensors',
  actuators: 'Actuators',
  other: 'Other',
}

const GROUP_ORDER = ['valves', 'pumps', 'sensors', 'actuators', 'other']

/**
 * Dock washing SKUs (401-0 / 401-01 / 401-1) always expose Linak 33 charging actuator in UI.
 * Firmware may omit the key until START/STOP_CHARGING runs — still show the row as "—".
 */
function withExpectedDockEquipment(
  equipment: IStationEquipmentTelemetry,
  lotModelOrLegacyType?: string | number | null
): IStationEquipmentTelemetry {
  const lotModel = typeof lotModelOrLegacyType === 'string' ? lotModelOrLegacyType : null
  if (!supportsExtendedDockSteps(lotModel)) {
    return equipment
  }
  if (Object.prototype.hasOwnProperty.call(equipment, 'charger_linak')) {
    return equipment
  }
  return { ...equipment, charger_linak: null }
}

/** Ordered groups for Equipment UI (by lot.lot_model when known). */
export function groupEquipmentEntries(
  equipment: IStationEquipmentTelemetry,
  lotModelOrLegacyType?: string | number | null
): EquipmentDisplayGroup[] {
  const equipmentForDisplay = withExpectedDockEquipment(equipment, lotModelOrLegacyType)
  let preferred: readonly string[]
  if (typeof lotModelOrLegacyType === 'number') {
    preferred =
      lotModelOrLegacyType === LOT_TYPE_REFILL
        ? REFILL_ORDER
        : lotModelOrLegacyType === LOT_TYPE_WASHING
          ? WASHING_ORDER
          : [...REFILL_ORDER, ...WASHING_ORDER]
  } else {
    const profile = profileFromLotModel(lotModelOrLegacyType)
    preferred =
      profile === 'refill'
        ? REFILL_ORDER
        : profile === 'washing'
          ? WASHING_ORDER
          : [...REFILL_ORDER, ...WASHING_ORDER]
  }

  const keys = sortKeys(
    Object.keys(equipmentForDisplay).filter(
      key => equipmentForDisplay[key] !== undefined && !isLegacyEquipmentKey(key)
    ),
    preferred
  )

  const buckets = new Map<string, Array<[string, StationEquipmentValue]>>()
  for (const key of keys) {
    const gid = groupIdForKey(key)
    const list = buckets.get(gid) || []
    list.push([key, equipmentForDisplay[key]])
    buckets.set(gid, list)
  }

  return GROUP_ORDER.filter(id => buckets.has(id)).map(id => ({
    id,
    title: GROUP_TITLES[id] || id,
    entries: buckets.get(id)!,
  }))
}

export function formatEquipmentValue(value: StationEquipmentValue): string {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no'
  }
  return String(value)
}
